import { apiRequest } from './api';
import { readStorageJSON } from './storage';

const AUTH_USER_CACHE_KEY = 'payrocky_auth_user';

function base64UrlToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function registerPushServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('This browser does not support service workers.');
  }
  return navigator.serviceWorker.register('/sw.js');
}

export async function unregisterPushServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return false;
  }
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!registration) {
    return false;
  }
  await registration.unregister();
  return true;
}

export function hasStoredAdminSession() {
  const cachedAuth = readStorageJSON(AUTH_USER_CACHE_KEY, null);
  const role = cachedAuth?.value?.role || cachedAuth?.role || '';
  return String(role).trim().toLowerCase() === 'admin';
}

export async function getPushSupportState() {
  const supportsNotifications = typeof window !== 'undefined' && 'Notification' in window;
  const supportsServiceWorker = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  const supportsPushManager = typeof window !== 'undefined' && 'PushManager' in window;

  return {
    supported: supportsNotifications && supportsServiceWorker && supportsPushManager,
    permission: supportsNotifications ? Notification.permission : 'denied',
  };
}

export async function enableAdminPushNotifications() {
  const supportState = await getPushSupportState();
  if (!supportState.supported) {
    throw new Error('This device does not support web push notifications.');
  }

  const permission = supportState.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();

  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted on this device.');
  }

  const registration = await registerPushServiceWorker();
  const keyRes = await apiRequest('/admin/push/public-key');
  const applicationServerKey = base64UrlToUint8Array(keyRes.publicKey);
  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription = existingSubscription || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });

  const res = await apiRequest('/admin/push/subscribe', {
    method: 'POST',
    body: {
      subscription: subscription.toJSON(),
      deviceName: navigator.platform || navigator.userAgent || 'Admin device',
    },
  });

  return {
    subscription,
    settings: res.settings,
    message: res.message,
  };
}

export async function disableAdminPushNotifications() {
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  const subscription = await registration?.pushManager.getSubscription();

  if (!subscription) {
    return { message: 'Push notifications are already disabled on this device.' };
  }

  await apiRequest('/admin/push/unsubscribe', {
    method: 'POST',
    body: { endpoint: subscription.endpoint },
  });

  await subscription.unsubscribe();

  return { message: 'Push notifications disabled on this device.' };
}
